import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AiOutlineEdit, AiOutlineDelete, AiOutlinePlus, AiOutlineCheck, AiOutlineStop, AiOutlineUp, AiOutlineHome } from 'react-icons/ai';
import DocTreeSidebar, { buildTreeFromFlat, findPath as findTreePath } from '../DocTreeSidebar/DocTreeSidebar';

/**
 * Vista contextual de ACLs (#57 follow-up): el admin elige un nodo en el árbol
 * documental (folder o serie) y ve TRES bloques:
 *
 *   - **Efectiva**: grupos que el motor de búsqueda usa como `allowed_groups`
 *     para los docs de este subárbol (resultado de la semántica A.2).
 *   - **Locales**: ACL entries con `resource_id = nodeId`. Editables/borrables.
 *   - **Heredadas**: entries definidas en ancestros que se aplican a este nodo.
 *     Read-only desde acá; para modificarlas, el admin tiene que navegar al
 *     ancestro correspondiente (link directo).
 *
 * Para cada operación de CRUD, antes de confirmar se muestra un warning con la
 * cantidad de series descendientes afectadas (cascade reindex).
 *
 * Props:
 * - folders, series: listas planas (folders ya viene de /admin/folders, series
 *   de /series?scope=all). El componente arma el árbol con buildTreeFromFlat.
 * - audit / impact / loading / error: estado controlado por el padre.
 * - selectedNodeId, onSelectNode: nodo actualmente seleccionado.
 * - onCreate, onEdit, onDelete, onNavigateToAncestor: callbacks del padre.
 */
const AclContextualView = ({
  folders,
  series,
  audit,
  loading,
  selectedNodeId,
  onSelectNode,
  onCreate,
  onEdit,
  onDelete,
  onNavigateToAncestor,
}) => {
  const tree = useMemo(
    () => buildTreeFromFlat(folders || [], series || []),
    [folders, series]
  );

  const selectedPath = useMemo(() => {
    if (!selectedNodeId) return [];
    return findTreePath(tree, selectedNodeId) || [];
  }, [selectedNodeId, tree]);

  const handleTreeClick = (path) => {
    if (!path || path.length === 0) return;
    const last = path[path.length - 1];
    onSelectNode(last.id);
  };

  return (
    <div style={styles.container}>
      <div style={styles.treePanel} data-testid="acl-tree-panel">
        <DocTreeSidebar
          folders={tree}
          selectedPath={selectedPath}
          onFolderClick={handleTreeClick}
          title="Árbol documental"
          storageKey="boveda.acl.expanded"
          noFetchCounts
          emptyMessage="No hay folders ni series"
          containerStyle={{ width: '100%', borderRight: 'none', backgroundColor: '#FFFFFF', padding: '12px' }}
        />
      </div>
      <div style={styles.detailPanel} data-testid="acl-detail-panel">
        {!selectedNodeId ? (
          <EmptyHint />
        ) : loading ? (
          <div style={styles.loading}>Cargando auditoría…</div>
        ) : !audit ? (
          <div style={styles.error}>No se pudo cargar la auditoría del nodo.</div>
        ) : (
          <DetailContent
            audit={audit}
            onCreate={onCreate}
            onEdit={onEdit}
            onDelete={onDelete}
            onNavigateToAncestor={onNavigateToAncestor}
          />
        )}
      </div>
    </div>
  );
};

const EmptyHint = () => (
  <div style={styles.empty}>
    <h3 style={{ color: '#00468B' }}>Seleccioná un folder o serie</h3>
    <p style={{ color: '#6B7280' }}>
      Elegí cualquier nodo del árbol para ver sus ACLs locales, las heredadas de ancestros,
      y los grupos con acceso efectivo (heredencia A.2).
    </p>
  </div>
);

const DetailContent = ({ audit, onCreate, onEdit, onDelete, onNavigateToAncestor }) => (
  <div>
    <Breadcrumb audit={audit} onNavigate={onNavigateToAncestor} />

    <Section title={`Grupos con acceso efectivo (LIST) — ${audit.effectiveGroups.length}`}
             hint="Resultado del cálculo de herencia A.2: ⋃ ALLOWs − ⋃ DENYs en la cadena del nodo.">
      {audit.effectiveGroups.length === 0 ? (
        <p style={styles.dim}>Ningún grupo tiene acceso efectivo a este nodo.</p>
      ) : (
        <div style={styles.chipRow}>
          {audit.effectiveGroups.map((g) => (
            <span key={g} style={styles.chipAllow} data-testid={`effective-group-${g}`}>
              <AiOutlineCheck /> {g}
            </span>
          ))}
        </div>
      )}
    </Section>

    <Section
      title={`ACLs locales — ${audit.local.length}`}
      hint="Definidas directamente en este nodo. Las podés modificar/borrar desde acá."
      action={(
        <button style={styles.addBtn} onClick={() => onCreate(audit.nodeId)} data-testid="acl-add-local">
          <AiOutlinePlus /> Nueva ACL local
        </button>
      )}
    >
      {audit.local.length === 0 ? (
        <p style={styles.dim}>Este nodo no tiene ACLs locales.</p>
      ) : (
        <AclTable entries={audit.local} kind="local" onEdit={onEdit} onDelete={onDelete} />
      )}
    </Section>

    <Section
      title={`ACLs heredadas — ${audit.inherited.length}`}
      hint="Definidas en algún ancestro del nodo. Para editarlas, navegá al nodo de origen."
    >
      {audit.inherited.length === 0 ? (
        <p style={styles.dim}>Este nodo no hereda ninguna ACL.</p>
      ) : (
        <AclTable entries={audit.inherited} kind="inherited" onNavigateToAncestor={onNavigateToAncestor} />
      )}
    </Section>
  </div>
);

const Breadcrumb = ({ audit, onNavigate }) => (
  <nav style={styles.breadcrumb} aria-label="Path del nodo">
    <button style={styles.crumb} onClick={() => onNavigate(null)} title="Ir a raíz">
      <AiOutlineHome />
    </button>
    {audit.ancestors.map((a) => (
      <React.Fragment key={a.id}>
        <span style={styles.crumbSep}>/</span>
        <button style={styles.crumb} onClick={() => onNavigate(a.id)} title={a.id}>
          {a.name}
        </button>
      </React.Fragment>
    ))}
    <span style={styles.crumbSep}>/</span>
    <strong style={styles.crumbCurrent}>{audit.nodeName}</strong>
  </nav>
);

const Section = ({ title, hint, action, children }) => (
  <section style={styles.section}>
    <div style={styles.sectionHeader}>
      <div>
        <h4 style={styles.sectionTitle}>{title}</h4>
        {hint && <p style={styles.sectionHint}>{hint}</p>}
      </div>
      {action}
    </div>
    <div>{children}</div>
  </section>
);

const AclTable = ({ entries, kind, onEdit, onDelete, onNavigateToAncestor }) => (
  <table style={styles.table}>
    <thead>
      <tr>
        <th style={styles.th}>Sujeto</th>
        <th style={styles.th}>Efecto</th>
        <th style={styles.th}>Acciones</th>
        {kind === 'inherited' && <th style={styles.th}>Origen</th>}
        <th style={styles.th}>Vigencia</th>
        <th style={styles.thRight}></th>
      </tr>
    </thead>
    <tbody>
      {entries.map((e) => (
        <tr key={e.id} style={kind === 'inherited' ? styles.trInherited : undefined}
            data-testid={`acl-row-${kind}-${e.id}`}>
          <td style={styles.td}>
            <span style={styles.subjectBadge}>{e.subjectType}</span>{' '}
            <code>{e.subjectId}</code>
          </td>
          <td style={styles.td}>
            {e.effect === 'ALLOW' ? (
              <span style={styles.chipAllowSmall}><AiOutlineCheck /> ALLOW</span>
            ) : (
              <span style={styles.chipDenySmall}><AiOutlineStop /> DENY</span>
            )}
          </td>
          <td style={styles.td}>{(e.actions || []).join(', ')}</td>
          {kind === 'inherited' && (
            <td style={styles.td}>
              <button
                style={styles.linkBtn}
                onClick={() => onNavigateToAncestor && onNavigateToAncestor(e.sourceNodeId)}
                title={`Ver en el nodo ${e.sourceNodeId}`}
              >
                <AiOutlineUp /> {e.sourceNodeName}
              </button>
            </td>
          )}
          <td style={styles.td}>
            {e.timeFrom || e.timeTo
              ? `${e.timeFrom ? new Date(e.timeFrom).toLocaleDateString() : '∞'} → ${e.timeTo ? new Date(e.timeTo).toLocaleDateString() : '∞'}`
              : 'Permanente'}
          </td>
          <td style={styles.tdActions}>
            {kind === 'local' && (
              <>
                <button style={styles.iconBtn} onClick={() => onEdit(e)} title="Editar"
                        data-testid={`acl-edit-${e.id}`}>
                  <AiOutlineEdit />
                </button>
                <button style={{ ...styles.iconBtn, color: '#B91C1C' }} onClick={() => onDelete(e)} title="Eliminar"
                        data-testid={`acl-delete-${e.id}`}>
                  <AiOutlineDelete />
                </button>
              </>
            )}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const styles = {
  container: { display: 'grid', gridTemplateColumns: '320px 1fr', gap: '20px', minHeight: '600px' },
  treePanel: { background: '#FFFFFF', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' },
  detailPanel: { background: '#FFFFFF', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '20px' },
  loading: { textAlign: 'center', color: '#6B7280', padding: '40px' },
  error: { color: '#B91C1C', padding: '20px' },
  empty: { textAlign: 'center', padding: '40px 20px' },
  breadcrumb: { display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', marginBottom: '20px', padding: '8px 0', borderBottom: '1px solid #E5E7EB' },
  crumb: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#00468B', fontSize: '13px', padding: '2px 6px', borderRadius: '4px' },
  crumbSep: { color: '#9CA3AF', fontSize: '13px' },
  crumbCurrent: { color: '#111827', fontSize: '14px', padding: '2px 6px' },
  section: { marginBottom: '30px' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' },
  sectionTitle: { margin: 0, fontSize: '15px', color: '#00468B', fontWeight: 'bold' },
  sectionHint: { margin: '4px 0 0', fontSize: '12px', color: '#6B7280' },
  dim: { color: '#9CA3AF', fontSize: '13px', margin: '8px 0' },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  chipAllow: { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', background: '#E8F5E9', color: '#2E7D32', borderRadius: '12px', fontSize: '12px', fontWeight: '600', border: '1px solid #C8E6C9' },
  chipAllowSmall: { display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '2px 6px', background: '#E8F5E9', color: '#2E7D32', borderRadius: '10px', fontSize: '11px', fontWeight: '600' },
  chipDenySmall: { display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '2px 6px', background: '#FEE2E2', color: '#991B1B', borderRadius: '10px', fontSize: '11px', fontWeight: '600' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { textAlign: 'left', padding: '8px', borderBottom: '1px solid #E5E7EB', color: '#374151', fontWeight: '600' },
  thRight: { textAlign: 'right', padding: '8px', borderBottom: '1px solid #E5E7EB' },
  td: { padding: '8px', borderBottom: '1px solid #F3F4F6', color: '#374151' },
  tdActions: { padding: '8px', borderBottom: '1px solid #F3F4F6', textAlign: 'right' },
  trInherited: { background: '#FAFAFA' },
  subjectBadge: { display: 'inline-block', fontSize: '10px', padding: '1px 5px', borderRadius: '3px', background: '#E5E7EB', color: '#374151' },
  linkBtn: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#00468B', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px' },
  iconBtn: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#00468B', fontSize: '15px', padding: '4px 6px' },
  addBtn: { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: '#00468B', color: '#FFFFFF', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
};

export default AclContextualView;
